import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CREATE_NEW_PROSPECT_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Form, Loader } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';
import { useCreateProspect } from '../../../api/services/lupp/prospect/prospect';
import type {
  CreateProspectRequest,
  CreateProspectResponse,
  TestDriveSchedule,
} from '../../../api/services/lupp/prospect/dto.prospect';
import { useLabels } from '../../../hooks/useLabels';
import { convertToContentfulLocale, formatToUTCDate, localToUTC } from '../../../utils/tools';
import { createLeadFormFields } from './formFields';
import styles from './CreateNewProspect.module.scss';

const REDIRECT_DELAY = 3000;
const REDIRECT_PATH = '/prospect-desk/prospects';

/**
 * Builds test drive schedule from form data
 * The Form component passes data keyed by apiKey, so we use those keys here
 */
const buildTestDriveSchedule = (
  formData: Record<string, unknown>,
  location: string,
  locale: string,
  isTestDriveFlag: boolean
): TestDriveSchedule[] | undefined => {
  const isTestDrive = formData.isTestDrive as string[] | undefined;
  if (!isTestDrive?.includes('yes')) return undefined;

  const timeSlot = (formData.testDriveTime as string) || '9:00-9:30';
  const [startTime, endTime] = timeSlot.split('-');
  const testDriveDate = formData.testDriveDate as string | Date | undefined;
  // Use formatToUTCDate to get YYYY-MM-DD format, then localToUTC converts studio time to UTC
  const baseDateStr = testDriveDate ? formatToUTCDate(testDriveDate) : formatToUTCDate(new Date());

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  return [
    {
      appointmentLocationName: location,
      scheduledStartDatetime: localToUTC(baseDateStr, startHour, startMinute, locale),
      scheduledEndDatetime: localToUTC(baseDateStr, endHour, endMinute, locale),
      testDriveStatus: (formData.status as string) || 'scheduled',
      trim: (formData.selectedModelTrim as string) || '',
      isActive: isTestDriveFlag,
      isDeleted: false,
    },
  ];
};

const Create: React.FC = () => {
  const { userIdentity } = useStore();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const navigate = useLocaleNavigate();
  const { mutate, isPending } = useCreateProspect();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [createdProspectId, setCreatedProspectId] = useState<string | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  const { data: labels } = useLabels({
    id: CREATE_NEW_PROSPECT_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  const handleCancel = useCallback(() => {
    navigate(REDIRECT_PATH);
  }, [navigate]);

  const location =
    userIdentity?.city && userIdentity?.countryCode
      ? `${userIdentity.city}, ${userIdentity.countryCode}`
      : userIdentity?.city || userIdentity?.countryCode || '—';
  const organizationName = userIdentity?.organizationName;

  const formFields = useMemo(
    () =>
      createLeadFormFields(
        {
          onCancel: handleCancel,
          location,
          organizationName,
        },
        labels,
        locale
      ),
    [handleCancel, location, organizationName, labels, locale]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        // Navigate to the created prospect if we have an ID, otherwise go to prospects list
        const redirectPath = createdProspectId
          ? `/prospect-desk/prospects/${createdProspectId}`
          : REDIRECT_PATH;
        navigate(redirectPath);
      }, REDIRECT_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage, navigate, createdProspectId]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    setErrorMessage(null);
    const formData = data as Record<string, unknown>;
    const isTestDrive = (formData.isTestDrive as string[])?.includes('yes') ?? false;

    const { isEmailOptIn, callConsent, ...restFormData } = formData;

    const payload = {
      ...restFormData,
      isEmailOptIn: (isEmailOptIn as string[])?.includes('emailOptIn') || null,
      isCallOptIn: callConsent === 'callOptIn' || null,
      isCallOptOut: callConsent === 'callOptOut' || null,
      isTestDrive,
      prospectOwnerLocation: location,
      testDriveSchedules: buildTestDriveSchedule(formData, location || '', locale, isTestDrive),
    } as CreateProspectRequest;

    mutate(payload, {
      onSuccess: (response: CreateProspectResponse) => {
        if (response.prospectIdentifier) {
          setCreatedProspectId(response.prospectIdentifier);
        }
        setSuccessMessage(
          labels?.['create.success'] ?? 'Prospect created successfully! Redirecting...'
        );
      },
      onError: (error: unknown) => {
        setSuccessMessage('');
        // API errors are returned as strings from the LUPP service
        const errorMsg =
          typeof error === 'string'
            ? error
            : error instanceof Error
              ? error.message
              : (labels?.['create.error'] ?? 'Failed to create prospect. Please try again.');
        setErrorMessage(errorMsg);
      },
    });
  };

  const handleError = () => {
    setErrorMessage(
      labels?.['create.validationError'] ?? 'Please fix the form errors before submitting.'
    );
    setSuccessMessage('');
  };

  return (
    <div ref={messageRef}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.['create.createNewProspect'] ?? 'Create New Prospect'}
      </Typography>
      <Box pt={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          {labels?.['create.addANew'] ?? 'Add a new prospect to your sales funnel'}
        </Typography>
      </Box>
      <Box className={styles.formContent} background={theme === 'dark' ? 'white' : 'black'} mt={32}>
        <Box className={styles.formWrapper}>
          {successMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {successMessage}
              </Typography>
            </Box>
          )}
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isPending && (
            <Box className="fetchingOverlay">
              <Loader size={32} theme={theme === 'dark' ? 'light' : 'dark'} />
            </Box>
          )}
          <Form
            id="create-prospect-form"
            ref={formRef}
            theme={theme === 'dark' ? 'light' : 'dark'}
            formFields={formFields}
            onSuccess={handleSuccess}
            onError={handleError}
            isFluid={true}
            locale={locale}
            language={language ?? locale.split('-')[0]}
          />
        </Box>
      </Box>
    </div>
  );
};

export default Create;
